import type { GetSrcArgs } from 'openimg/react'

type TargetFormat = 'webp' | 'jpg' | 'png'

// Default R2 bucket URL placeholder - will be set by context provider in root.tsx
let R2_BUCKET_URL = 'https://your-r2-bucket.r2.dev'

// Default image quality per format - optimized for better performance
const DEFAULT_QUALITY: Record<TargetFormat, number> = {
  webp: 80, // Good compression with high quality
  jpg: 80,  // Standard quality for photos
  png: 85   // Higher quality for graphics
}

/**
 * Optimized image source generator for various image sources
 * Handles different image sources differently to prevent optimization errors
 */
export const getSrc = ({
  src,
  width,
  height,
  fit,
  format = 'webp',
  optimizerEndpoint
}: GetSrcArgs): string => {
  if (!src) return ''

  // Skip processing for data: URLs and missing optimizerEndpoint
  if (src.startsWith('data:') || !optimizerEndpoint) {
    return src
  }

  // For external images (Imgur, Google Drive), use direct URLs to avoid processing errors
  if (src.includes('imgur.com')) {
    // Convert to direct imgur URL if needed
    const imgurId = src.split('/').pop()?.split('.')[0]
    if (imgurId) {
      return `https://i.imgur.com/${imgurId}.jpg`
    }
    return src
  }

  if (src.includes('drive.google.com')) {
    // Extract file ID from Google Drive URL
    const fileId = src.match(/\/d\/([^\/]+)/)?.[1] || src.match(/id=([^&]+)/)?.[1]
    if (fileId) {
      return `https://drive.google.com/uc?export=view&id=${fileId}`
    }
    return src
  }

  // For performance, limit max dimensions if not specified
  const maxWidth = width && width > 0 ? width : 1200
  const maxHeight = height && height > 0 ? height : 1200

  // For query params we'll add to all image requests
  const params = new URLSearchParams({
    w: maxWidth.toString(),
    h: maxHeight.toString(),
  })

  // Add optional parameters
  if (fit) params.append('fit', fit)
  if (format) params.append('format', format)

  // Add quality based on format - lower quality for better performance
  if (format && DEFAULT_QUALITY[format as TargetFormat]) {
    params.append('q', DEFAULT_QUALITY[format as TargetFormat].toString())
  }

  // Handle local images - these should work with our optimizer
  if (src.startsWith('/img/')) {
    // For local images in the public directory, bypass the optimizer
    // This prevents 404 errors when images are publicly accessible
    return src;

    // Uncomment below to use optimizer (if working correctly)
    // params.append('src', src)
    // return `${optimizerEndpoint}?${params.toString()}`
  }

  // Handle R2 images
  if (src.startsWith(R2_BUCKET_URL)) {
    params.append('src', src)
    return `${optimizerEndpoint}?${params.toString()}`
  }

  // For all other URLs, skip the optimizer to avoid fetch errors
  return src
}

// Default breakpoints based on Tailwind's breakpoints - more granular for better responsive support
export const defaultBreakpoints = [320, 480, 640, 768, 1024, 1280, 1536]

// Default formats to support in preference order - prioritize webp for better performance
export const defaultFormats: TargetFormat[] = ['webp', 'jpg']

/**
 * Helper to get image dimensions with a given aspect ratio
 * Useful for maintaining consistent image dimensions across the site
 */
export function getImageDimensions(
  width: number,
  height: number,
  aspectRatio?: number
): { width: number; height: number } {
  if (aspectRatio) {
    // If we have an aspect ratio, calculate the height based on width
    return {
      width,
      height: Math.round(width / aspectRatio),
    }
  }
  return { width, height }
}

/**
 * Standard aspect ratios for common use cases
 */
export const ASPECT_RATIOS = {
  landscape: 16 / 9,
  square: 1,
  portrait: 3 / 4,
  card: 2 / 3,
  wide: 21 / 9,
}

/**
 * Export a setter function for R2 bucket URL that can be called from root.tsx
 * This allows the server to provide the correct URL without requiring direct imports
 */
export function setR2BucketUrl(url: string) {
  R2_BUCKET_URL = url
} 